import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  HoverActionField,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@klareospace/pharos-lib-web-utils';

import type { OrderFieldEditState, OrderFieldLockReason } from '../hooks/use-order-editability';
import { ORDER_FIELD_EDIT_STATE } from '../hooks/use-order-editability';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EditableFieldWrapperProps {
  readonly children: React.ReactNode;
  readonly fieldState: OrderFieldEditState;
  readonly lockReason: OrderFieldLockReason | null;
  readonly onEdit: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Renders a drawer field's display value with the correct affordance for its
 * `OrderFieldEditState`: hidden (no permission, or a terminal order — plain read-only,
 * no pencil at all), locked (disabled pencil + tooltip explaining why — the in_progress
 * lock — or plain read-only when `lockReason` is null, i.e. a never-editable category
 * with nothing actually blocking it), or editable (clickable pencil opens the editor).
 * Shared by every drawer field gated by `useOrderEditability` (time window, carrier,
 * content inline edits).
 */
export function EditableFieldWrapper({
  children,
  fieldState,
  lockReason,
  onEdit,
}: EditableFieldWrapperProps): React.JSX.Element {
  const { t } = useTranslation('orders');

  if (fieldState === ORDER_FIELD_EDIT_STATE.HIDDEN || (fieldState === ORDER_FIELD_EDIT_STATE.LOCKED && !lockReason)) {
    return <HoverActionField>{children}</HoverActionField>;
  }

  if (fieldState === ORDER_FIELD_EDIT_STATE.LOCKED) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <HoverActionField disabled>{children}</HoverActionField>
            </div>
          </TooltipTrigger>
          <TooltipContent>{t('common.inProgressTooltip')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return <HoverActionField onClick={onEdit}>{children}</HoverActionField>;
}
